/*
 * HomeCrest PDP - Phase 1 behaviour
 * ===========================================================================
 * Two very small custom elements. Between them they cover every HomeCrest
 * addition to the product hero. Nothing here duplicates Horizon: variant
 * resolution, the gallery, add-to-cart and dynamic checkout are untouched.
 *
 * <hc-variant-sync data-hc-sync="ID">
 *   Re-renders its own contents when the selected variant changes.
 *
 *   Horizon's variant picker already fetches the whole section for the new
 *   variant and resolves `shopify:product:select` with the parsed document, so
 *   this listens to that event and lifts its own markup out of the response.
 *   That means NO extra network request, and the markup is produced by Liquid
 *   with the real variant in scope - so a price, an EMI figure or a delivery
 *   window can never drift out of step with what Shopify will actually charge.
 *
 * <hc-expandable>
 *   The "Read more" control on the short description. The button stays hidden
 *   unless the text genuinely overflows its clamp, so it never offers content
 *   that is not there.
 *
 * Written as a plain module with no imports so it does not depend on Horizon's
 * import map.
 */

const PRODUCT_SELECT = 'shopify:product:select';

class HcVariantSync extends HTMLElement {
  /** @type {EventTarget | null} */
  #scope = null;

  connectedCallback() {
    /*
     * Listen on `document`, not on the closest section.
     *
     * The event is dispatched on the variant picker inside the product hero
     * and bubbles up from there. A sync element in a DIFFERENT section - the
     * full-width details section below the hero, for one - is a sibling of
     * that section, so the event never passes through it. Binding at the
     * document catches it wherever the element lives.
     *
     * Breadth is safe here because #onProductSelect discards anything that is
     * not this product: it ignores product-card pickers, bails on a product id
     * mismatch, and bails again if it cannot find itself in the response.
     */
    this.#scope = document;
    this.#scope.addEventListener(PRODUCT_SELECT, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(PRODUCT_SELECT, this.#onProductSelect);
    this.#scope = null;
  }

  #onProductSelect = (event) => {
    // Product cards and quick add dialogs raise the same event; those are not
    // this element's product.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((result) => {
        const detail = result?.detail;
        const html = detail?.html;
        if (!html) return;

        // A combined listing can swap in a different product. Follow it.
        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const id = this.dataset.hcSync;
        if (!id) return;

        const replacement = html.querySelector(`hc-variant-sync[data-hc-sync="${CSS.escape(id)}"]`);
        // Absent means the response is for something else; leaving the current
        // markup alone is safer than blanking it.
        if (!replacement) return;

        if (this.innerHTML !== replacement.innerHTML) {
          this.innerHTML = replacement.innerHTML;
        }

        // The block renders an empty wrapper when it has nothing to say, so
        // the element must collapse rather than leave a gap in the stack.
        this.toggleAttribute('hidden', replacement.children.length === 0);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[homecrest-pdp] variant sync failed:', error);
      });
  };
}

class HcExpandable extends HTMLElement {
  #resizeObserver = null;

  connectedCallback() {
    this.#sync();

    const toggle = this.querySelector('[data-hc-expand-toggle]');
    toggle?.addEventListener('click', this.#onToggle);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#sync());
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.querySelector('[data-hc-expand-toggle]')?.removeEventListener('click', this.#onToggle);
  }

  #onToggle = () => {
    const expanded = this.dataset.expanded === 'true';
    this.dataset.expanded = expanded ? 'false' : 'true';

    const toggle = this.querySelector('[data-hc-expand-toggle]');
    if (!(toggle instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', String(!expanded));
    const next = expanded ? toggle.dataset.labelMore : toggle.dataset.labelLess;
    if (next) toggle.textContent = next;
  };

  /** Show the toggle only when the clamped text is actually taller than its box. */
  #sync() {
    const body = this.querySelector('[data-hc-expand-body]');
    const toggle = this.querySelector('[data-hc-expand-toggle]');
    if (!(body instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;
    if (this.dataset.expanded === 'true') return;

    const overflows = body.scrollHeight - body.clientHeight > 2;
    toggle.toggleAttribute('hidden', !overflows);
  }
}

/*
 * Hooks for the parts of the design that have no implementation yet.
 *
 * These are deliberately event dispatchers and nothing more. They do not open
 * a viewer, quote a rate or start a configurator, because none of those exist
 * in this store yet. When the real components arrive they listen for these
 * events on `document` and the markup does not have to change.
 *
 * One delegated listener for the whole page, bound once.
 */
const HOOKS = [
  ['[data-homecrest-spin]', 'homecrest:media:spin'],
  ['[data-homecrest-emi]', 'homecrest:emi:open'],
  ['[data-homecrest-action]', 'homecrest:action'],
];

function bindHooks() {
  if (document.documentElement.dataset.hcPdpHooks === 'true') return;
  document.documentElement.dataset.hcPdpHooks = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    for (const [selector, name] of HOOKS) {
      const trigger = target.closest(selector);
      if (!trigger) continue;

      // Items configured with a URL are ordinary links; let them navigate.
      if (trigger instanceof HTMLAnchorElement && trigger.getAttribute('href')) return;

      document.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: {
            trigger,
            action: trigger.getAttribute('data-homecrest-action') ?? undefined,
            source: trigger.getAttribute('data-spin-source') ?? undefined,
            productHandle: trigger.getAttribute('data-product-handle') ?? undefined,
            variantId: trigger.getAttribute('data-variant-id') ?? undefined,
          },
        })
      );
      return;
    }
  });
}

bindHooks();

/* ---------------------------------------------------------------------------
 * Shopify 3D / AR bridge
 *
 * ONE source of truth: the product's first `model` media, resolved in Liquid
 * as `product.media | where: 'media_type', 'model' | first`. Every HomeCrest
 * entry point - the gallery overlay, the 360 rail and Make it yours - renders
 * a NATIVE `data-shopify-xr` button carrying that model id, so Shopify's own
 * SDK is what launches AR. Nothing here re-implements AR or 3D.
 *
 * Two real defects this repairs:
 *
 *   1. Horizon calls `ShopifyXR.setupXRElements()` exactly once, from the
 *      gallery, and then REMOVES the ProductModelJSON script. Any XR button
 *      entering the DOM after that - Make it yours further down the page, or
 *      anything re-rendered by a theme-editor section reload - is never bound
 *      and keeps `data-shopify-xr-hidden`, so it stays invisible. We watch for
 *      XR buttons and re-run setup, debounced and idempotent.
 *
 *   2. The 360 card used to be a REMOTE CONTROL for the gallery: it selected
 *      the gallery slide and scrolled the page up. That is gone. The card now
 *      renders its own <model-viewer> inline (see the 360 rail in
 *      sections/homecrest-product-details.liquid); this file only pulls in
 *      Shopify's model-viewer bundle, wires loading/error state, and routes AR
 *      at that same element. A product with a real `homecrest.spin_360`
 *      sequence still dispatches the untouched `homecrest:media:spin` event
 *      for a dedicated viewer to answer - a GLB is never faked into a spin.
 * ------------------------------------------------------------------------- */

const XR_SELECTOR = '[data-shopify-xr][data-shopify-model3d-id]';
let xrTimer = null;
let xrWaiting = false;

/** Shopify's XR SDK is present and usable. */
function xrReady() {
  return Boolean(window.ShopifyXR && typeof window.ShopifyXR.setupXRElements === 'function');
}

/** Wait for the SDK once, then re-bind. Never stacks listeners. */
function waitForXR() {
  if (xrWaiting) return;
  xrWaiting = true;
  document.addEventListener(
    'shopify_xr_initialized',
    () => {
      xrWaiting = false;
      scheduleXR();
    },
    { once: true }
  );
}

/**
 * Re-bind every XR button on the page. The SDK keeps the models it was handed
 * by `addModels`, so binding late costs nothing and repeats safely: it simply
 * re-scans and clears `data-shopify-xr-hidden` wherever AR is possible.
 */
function refreshXR() {
  if (!document.querySelector(XR_SELECTOR)) return;

  if (xrReady()) {
    try {
      window.ShopifyXR.setupXRElements();
    } catch (error) {
      console.warn('[homecrest] ShopifyXR.setupXRElements failed', error);
    }
    return;
  }

  // The gallery owns loading the SDK. Ask it rather than racing it.
  if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
    try {
      window.ProductModel.loadShopifyXR();
    } catch (error) {
      console.warn('[homecrest] ProductModel.loadShopifyXR failed', error);
    }
  }
  waitForXR();
}

/** Collapse bursts of DOM changes into a single re-bind. */
function scheduleXR() {
  if (xrTimer) clearTimeout(xrTimer);
  xrTimer = setTimeout(() => {
    xrTimer = null;
    refreshXR();
  }, 120);
}

function bindXRBridge() {
  if (document.documentElement.dataset.hcXrBridge === 'true') return;
  document.documentElement.dataset.hcXrBridge = 'true';

  scheduleXR();

  // Theme-editor section reloads and Horizon's variant re-render both replace
  // markup that can contain XR buttons.
  for (const name of ['shopify:section:load', 'shopify:block:select', 'variant:update']) {
    document.addEventListener(name, scheduleXR);
  }

  if (!document.body) return;

  // childList only. setupXRElements mutates attributes, so observing
  // attributes here would feed itself in a loop.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(XR_SELECTOR) || node.querySelector?.(XR_SELECTOR)) {
          scheduleXR();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

/* --- inline 3D viewer + AR ------------------------------------------- */

/** Wrapper the 360 card renders around its <model-viewer>. */
const MODEL_HOST = '[data-hc-model-viewer]';

/**
 * HomeCrest's own AR entry points. Horizon's gallery button is deliberately
 * NOT in this list: it stays on ShopifyXR, so the two mechanisms never both
 * bind the same control.
 */
const HC_AR_BUTTONS = '.hc-media-tool[data-shopify-xr], .hc-miy__cta[data-shopify-xr], [data-hc-ar]';

/** The inline viewer AR launches from, once it reports that it can. */
let arViewer = null;

const modelFeatureQueue = [];
let modelFeatureState = 'idle'; // idle | loading | ready | failed

function flushModelQueue() {
  while (modelFeatureQueue.length > 0) {
    const fn = modelFeatureQueue.shift();
    try {
      fn();
    } catch (error) {
      console.warn('[homecrest] model viewer callback failed', error);
    }
  }
}

/**
 * Pull in Shopify's model-viewer bundle once. This is the SAME feature that
 * Horizon's <product-model> loads - no second 3D library is shipped.
 */
function whenModelViewerReady(callback) {
  if (modelFeatureState === 'ready' || customElements.get('model-viewer')) {
    modelFeatureState = 'ready';
    callback();
    return;
  }

  modelFeatureQueue.push(callback);
  if (modelFeatureState === 'loading') return;

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    modelFeatureState = 'failed';
    flushModelQueue();
    return;
  }

  modelFeatureState = 'loading';
  window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }], (errors) => {
    modelFeatureState = errors ? 'failed' : 'ready';
    flushModelQueue();
  });
}

/**
 * Reveal HomeCrest's AR buttons only where AR can genuinely launch, and point
 * them at the inline viewer. They are taken off ShopifyXR first so the two
 * mechanisms cannot both fire - that dependency is exactly what used to leave
 * them stuck behind `data-shopify-xr-hidden`.
 *
 * @param {Element|null} viewer - The inline <model-viewer>, once upgraded.
 */
function syncArButtons(viewer) {
  const canActivate = Boolean(viewer && viewer.canActivateAR);
  arViewer = canActivate ? viewer : null;

  for (const button of document.querySelectorAll(HC_AR_BUTTONS)) {
    button.removeAttribute('data-shopify-xr');
    button.dataset.hcAr = 'true';

    if (canActivate) {
      button.removeAttribute('data-shopify-xr-hidden');
    } else {
      button.setAttribute('data-shopify-xr-hidden', '');
    }
  }
}

/**
 * canActivateAR is resolved by model-viewer shortly after the element upgrades
 * and does NOT wait for the GLB to download. There is no event for it, so we
 * re-check a few times across the first couple of seconds. Gating AR on the
 * model's 'load' event instead would mean a customer who never scrolls down to
 * the 360 card never sees a View in Room button at all, because the viewer
 * lazy-loads.
 *
 * @param {Element} viewer
 * @param {number} [attempt]
 */
function pollArCapability(viewer, attempt = 0) {
  syncArButtons(viewer);
  if (viewer.canActivateAR || attempt >= 6) return;
  setTimeout(() => pollArCapability(viewer, attempt + 1), 400);
}

/** Move a card out of its loading state. */
function setModelState(host, state) {
  host.setAttribute('data-hc-model-state', state);
  host.querySelector('[data-hc-model-loading]')?.toggleAttribute('hidden', true);
  host.querySelector('[data-hc-model-error]')?.toggleAttribute('hidden', state !== 'error');
}

/**
 * Bring every inline 360 card to life. Idempotent per host, so a theme-editor
 * section reload only initialises what is genuinely new.
 */
function initInlineModels() {
  const fresh = document.querySelectorAll(MODEL_HOST + ':not([data-hc-model-init])');
  if (fresh.length === 0) return;

  for (const host of fresh) {
    host.setAttribute('data-hc-model-init', '');
    const viewer = host.querySelector('model-viewer');
    if (!viewer) {
      setModelState(host, 'error');
      continue;
    }

    // model_viewer_tag emits src / poster / alt / camera-controls but NOT
    // ios-src, and nothing that enables AR. The USDZ Shopify generated for
    // this product rides on the wrapper, so attach it for Quick Look.
    const iosSrc = host.getAttribute('data-hc-model-ios');
    if (iosSrc) viewer.setAttribute('ios-src', iosSrc);
    viewer.setAttribute('ar', '');
    // ORDER IS THE WHOLE FIX. model-viewer walks ar-modes left to right and
    // takes the FIRST mode the device claims to support. With 'webxr' first,
    // every real ARCore phone reports immersive-ar as supported and wins the
    // match, so Android took the WebXR path instead of Scene Viewer: an
    // in-page session that needs camera permission AND the full GLB already
    // downloaded, which on a lazy-loaded card far down the page it is not.
    // The tap did nothing. Emulators hide this because they report
    // immersive-ar unsupported and quietly fall through to Scene Viewer.
    //
    // scene-viewer first  -> Android gets Google's native placement app.
    // quick-look next     -> iOS matches here (scene-viewer is Android-only).
    // webxr last          -> only where neither native handler exists.
    viewer.setAttribute('ar-modes', 'scene-viewer quick-look webxr');
    viewer.setAttribute('camera-controls', '');
    // Vertical drags still scroll the page; horizontal drags rotate.
    viewer.setAttribute('touch-action', 'pan-y');
    // A slow turn after a beat. It signals "this is interactive" without a
    // caption, and because a GLB's authored facing varies per model it also
    // brings the front around without hard-coding a camera angle per product.
    // Any drag takes over immediately.
    viewer.setAttribute('auto-rotate', '');
    viewer.setAttribute('shadow-intensity', '1');

    viewer.addEventListener(
      'load',
      () => {
        setModelState(host, 'ready');
        syncArButtons(viewer);
      },
      { once: true }
    );

    viewer.addEventListener('error', () => setModelState(host, 'error'), { once: true });
  }

  whenModelViewerReady(() => {
    for (const host of document.querySelectorAll(MODEL_HOST)) {
      const viewer = host.querySelector('model-viewer');
      if (modelFeatureState === 'failed' || !viewer) {
        setModelState(host, 'error');
        continue;
      }
      pollArCapability(viewer);
    }
  });
}

function bindArBridge() {
  if (document.documentElement.dataset.hcArBridge === 'true') return;
  document.documentElement.dataset.hcArBridge = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-hc-ar="true"]');
    if (!button) return;

    // Resolve at click time instead of trusting whatever the capability poll
    // cached. canActivateAR can turn true after the poll window closes, and a
    // button the customer can see must never be a silent no-op.
    const viewer = arViewer || document.querySelector(MODEL_HOST + ' model-viewer');
    if (!viewer || !viewer.canActivateAR) {
      console.warn('[homecrest] AR unavailable at click time', {
        viewerPresent: Boolean(viewer),
        canActivateAR: Boolean(viewer && viewer.canActivateAR),
      });
      return;
    }

    event.preventDefault();
    // Must stay inside the user-gesture window: both Quick Look and Scene
    // Viewer launch by clicking an <a> that model-viewer generates, and the
    // browser drops that click if it is deferred past the gesture. So this is
    // called synchronously and its promise is deliberately not awaited.
    try {
      viewer.activateAR();
    } catch (error) {
      console.warn('[homecrest] activateAR failed', error);
    }
  });
}

bindXRBridge();
bindArBridge();
initInlineModels();
for (const name of ['shopify:section:load', 'shopify:block:select']) {
  document.addEventListener(name, initInlineModels);
}

if (!customElements.get('hc-variant-sync')) {
  customElements.define('hc-variant-sync', HcVariantSync);
}

if (!customElements.get('hc-expandable')) {
  customElements.define('hc-expandable', HcExpandable);
}
